import React, { useState, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import { Link } from "react-router-dom";
import Box from "@mui/material/Box";
import { Commute, TrainRounded } from "@mui/icons-material";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Avatar from "@mui/material/Avatar";
import Tooltip from "@mui/material/Tooltip";
import Container from "@mui/material/Container";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Badge from "@mui/material/Badge";
import Popover from "@mui/material/Popover";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";
import Chip from "@mui/material/Chip";
import { keyframes } from "@mui/system";
import { AuthContext } from "../contexts/AuthContext";
import { SharedDataContext } from "../contexts/SharedDataContext";

import {
  regularRoutes,
  adminRoutes,
  serviceProviderRoutes,
} from "../utils/routes";

const settings = ["Profile", "Logout"];

// Pulsing animation for the train notification badge
const pulseAnimation = keyframes`
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(0, 172, 172, 0.7);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 0 0 10px rgba(0, 172, 172, 0);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(0, 172, 172, 0);
  }
`;

function NavBar({ isAdminMode, toggleAdminMode }) {
  const { trainsInPortals } = useContext(SharedDataContext);
  const pages = regularRoutes.filter((page) => page.navBar);
  const adminPages = adminRoutes.filter((page) => page.navBar);
  const [anchorElNav, setAnchorElNav] = useState(null);
  const [anchorElUser, setAnchorElUser] = useState(null);
  const [anchorElTrain, setAnchorElTrain] = useState(null);
  const { isLoggedIn, member, isAdmin, logout, isServiceProvider, setMember, setIsAdmin, setIsServiceProvider, setHasFetched, setResetTrigger, setRefreshTrigger } =
    useContext(AuthContext);
  const firstName = member?.first_name || "";
  const lastName = member?.last_name || "";
  const navigate = useNavigate();

  // Add state for account switching
  const [currentAccount, setCurrentAccount] = useState(member?.acct_id || "");
  const [memberAccounts, setMemberAccounts] = useState([]);



  // Update accounts when member changes
  useEffect(() => {
    if (member && member.accounts) {
      setMemberAccounts(member.accounts);
      setCurrentAccount(member.acct_id);
    }
  }, [member]);

  // Handle account change
  const handleAccountChange = (event) => {
    const newAccountId = event.target.value;
    setCurrentAccount(newAccountId);

    // Call context method to switch account
    switchAccount(newAccountId);

    // Navigate to default page based on user role
    const account = member.accounts.find((acc) => acc.id === newAccountId);
    const isServiceProvider = account.account_type === "SERVICE_PROVIDER";
    if (isServiceProvider) {
      navigate("/admin");
    } else {
      navigate("/");
    }
  };

  const switchAccount = async (accountId) => {
    try {
      // Trigger reset of all contexts (similar to logout)
      setHasFetched(false);
      setResetTrigger(prev => prev + 1);

      setMember((prevUser) => ({
        ...prevUser,
        acct_id: accountId,
      }));

      // get account from member
      const account = member.accounts.find((acc) => acc.id === accountId);
      if (!account) {
        throw new Error("Account not found");
      }
      // Determine user roles based on selected account
      const isServiceProvider = account.account_type === "SERVICE_PROVIDER";
      const isAdmin = !isServiceProvider && 
        account.subscription?.role_code?.toUpperCase() === "ADMIN";

      // Update context with the selected account info
      setMember({...member, selected_account: account, acct_id: account.id});
      setIsAdmin(isAdmin);
      setIsServiceProvider(isServiceProvider);

      // Save to localStorage
      localStorage.setItem("member", JSON.stringify({...member, selected_account: account}));
      localStorage.setItem("isAdmin", JSON.stringify(isAdmin));
      localStorage.setItem("isServiceProvider", JSON.stringify(isServiceProvider));

      // Reset the trigger and then refresh to trigger new data fetch
      setTimeout(() => {
        setResetTrigger(0);
        setRefreshTrigger(prev => prev + 1);
      }, 100);

      return true;
    } catch (error) {
      console.error("Error switching accounts:", error);
      return false;
    }
  };

  const handleOpenNavMenu = (event) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleLogout = () => {
    logout(true); // Skip automatic redirect
    navigate("/login");
  };

  const handleAdminModeToggle = () => {
    if (isAdminMode) {
      toggleAdminMode();
      navigate("/"); // Redirect to regular dashboard
    } else {
      toggleAdminMode();
      navigate("/admin/"); // Redirect to admin dashboard
    }
  };

  const handleSettingClick = (setting) => {
    if (setting === "Logout") {
      handleLogout();
    } else if (setting === "Profile") {
      if (isServiceProvider) {
        navigate("/admin/profile")
      } else {
        navigate("/profile");
      }
    } else if (setting === "Admin Mode") {
      handleAdminModeToggle(); // Turn on admin mode
    }
    handleCloseUserMenu();
  };

  // Add "Admin Mode" to settings if the user is an admin and not already in admin mode
  const dynamicSettings =
    isAdmin && !isAdminMode ? ["Admin Mode", ...settings] : settings;

  const getInitials = (firstName, lastName) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`;
  };

  const handleOpenTrainPopover = (event) => {
    setAnchorElTrain(event.currentTarget);
  };

  const handleCloseTrainPopover = () => {
    setAnchorElTrain(null);
  };

  const openTrainPopover = Boolean(anchorElTrain);

  return (
    <AppBar position="static">
      <Container maxWidth="xl">
        <Toolbar disableGutters>
          {/* Mobile menu icon */}
          <Box sx={{ flexGrow: 0, display: { xs: 'flex', md: 'none' } }}>
            <IconButton
              size="large"
              aria-label="menu"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleOpenNavMenu}
              color="inherit"
            >
              <MenuIcon />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorElNav}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              open={Boolean(anchorElNav)}
              onClose={handleCloseNavMenu}
              sx={{
                display: { xs: 'block', md: 'none' },
              }}
            >
              {isLoggedIn &&
                (isServiceProvider
                  ? serviceProviderRoutes.filter(route => route.navBar).map((page) => (
                    <MenuItem key={page.name} onClick={handleCloseNavMenu} component={Link} to={page.path}>
                      <Typography textAlign="center">{page.name}</Typography>
                    </MenuItem>
                  ))
                  : isAdminMode
                  ? adminRoutes.filter(route => route.navBar).map((page) => (
                    <MenuItem key={page.name} onClick={handleCloseNavMenu} component={Link} to={page.path}>
                      <Typography textAlign="center">{page.name}</Typography>
                    </MenuItem>
                  ))
                  : regularRoutes.filter(route => route.navBar).map((page) => (
                    <MenuItem key={page.name} onClick={handleCloseNavMenu} component={Link} to={page.path}>
                      <Typography textAlign="center">{page.name}</Typography>
                    </MenuItem>
                  ))
                )
              }
            </Menu>
          </Box>

          {/* Logo for mobile - centered */}
          <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' }, justifyContent: 'center', alignItems: 'center' }}>
            <Box 
              component="img"
              src="/centraco_WhiteBlack.png"
              alt="CENTRACO Logo"
              onClick={() => navigate(isAdminMode || isServiceProvider ? '/admin' : '/')}
              sx={{
                height: 35,
                cursor: 'pointer',
              }}
            />
          </Box>

          {/* Logo for desktop */}
          <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
            <Box 
              component="img"
              src="/centraco_WhiteBlack.png"
              alt="CENTRACO Logo"
              onClick={() => navigate(isAdminMode || isServiceProvider ? '/admin' : '/')}
              sx={{
                height: 40,
                mr: 2,
                cursor: 'pointer',
              }}
            />
          </Box>
          
          {/* Desktop navigation */}
          <Box sx={{ flexGrow: 1, display: { xs: "none", md: "flex" } }}>
            {isLoggedIn &&
              (isServiceProvider
                ? serviceProviderRoutes
                    .filter((route) => route.navBar)
                    .map((page) => (
                      <Button
                        key={page.name}
                        sx={{ my: 2, color: "white", display: "block" }}
                        component={Link}
                        to={page.path}
                      >
                        {page.name}
                      </Button>
                    ))
                : isAdminMode
                ? adminRoutes
                    .filter((route) => route.navBar)
                    .map((page) => (
                      <Button
                        key={page.name}
                        sx={{ my: 2, color: "white", display: "block" }}
                        component={Link}
                        to={page.path}
                      >
                        {page.name}
                      </Button>
                    ))
                : regularRoutes
                    .filter((route) => route.navBar)
                    .map((page) => (
                      <Button
                        key={page.name}
                        sx={{ my: 2, color: "white", display: "block" }}
                        component={Link}
                        to={page.path}
                      >
                        {page.name}
                      </Button>
                    )))}
          </Box>
          
          {/* Account and profile controls - always visible on both mobile and desktop */}
          <Box sx={{ flexGrow: 0, display: 'flex', alignItems: 'center', gap: 2 }}>
            {isLoggedIn ? (
              <>
                {isAdminMode && (
                  <Button
                    color="inherit"
                    onClick={handleAdminModeToggle}
                    sx={{ mr: 2, display: { xs: 'none', sm: 'inline-flex' } }}
                  >
                    Exit Admin Mode
                  </Button>
                )}

                {/* Train in Portal Notification - only show for non-service providers */}
                {!isServiceProvider && trainsInPortals.hasTrains && (
                  <>
                    <Tooltip title="Trains in portals">
                      <IconButton
                        onClick={handleOpenTrainPopover}
                        sx={{
                          animation: `${pulseAnimation} 2s infinite`,
                          backgroundColor: 'rgba(0, 172, 172, 0.2)',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 172, 172, 0.3)',
                          },
                        }}
                      >
                        <Badge 
                          badgeContent={trainsInPortals.details.length} 
                          color="success"
                          sx={{
                            '& .MuiBadge-badge': {
                              backgroundColor: '#00acac',
                              color: '#fff',
                              fontWeight: 'bold',
                            }
                          }}
                        >
                          <TrainRounded sx={{ color: '#ffffff', fontSize: 28 }} />
                        </Badge>
                      </IconButton>
                    </Tooltip>
                    <Popover
                      open={openTrainPopover}
                      anchorEl={anchorElTrain}
                      onClose={handleCloseTrainPopover}
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                      }}
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                      }}
                      PaperProps={{
                        sx: {
                          mt: 1.5,
                          minWidth: 320,
                          maxWidth: 400,
                          borderRadius: 2,
                          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                          background: 'linear-gradient(135deg, #548dd4 0%, #49b6d6 100%)',
                          color: 'white',
                        }
                      }}
                    >
                      <Box sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                          <TrainRounded sx={{ fontSize: 32, color: '#ffffff' }} />
                          <Box>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              Active Trains
                            </Typography>
                            <Typography variant="caption" sx={{ opacity: 0.9 }}>
                              {trainsInPortals.details.length} {trainsInPortals.details.length === 1 ? 'train' : 'trains'} currently in a portal
                            </Typography>
                          </Box>
                        </Box>
                        <Divider sx={{ borderColor: 'rgba(255,255,255,0.2)', mb: 2 }} />
                        <List sx={{ p: 0 }}>
                          {trainsInPortals.details.map((train, index) => (
                            <ListItem
                              key={index}
                              sx={{
                                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                                backdropFilter: 'blur(10px)',
                                borderRadius: 2,
                                mb: index < trainsInPortals.details.length - 1 ? 1.5 : 0,
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                                py: 2,
                                px: 2,
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  backgroundColor: 'rgba(255, 255, 255, 0.25)',
                                  transform: 'translateX(4px)',
                                },
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', mb: 1 }}>
                                <ListItemIcon sx={{ minWidth: 40 }}>
                                  <Commute sx={{ color: '#ffffff', fontSize: 24 }} />
                                </ListItemIcon>
                                <ListItemText
                                  primary={train.portalName}
                                  primaryTypographyProps={{
                                    sx: { 
                                      fontWeight: 600, 
                                      fontSize: '1.05rem',
                                      color: 'white' 
                                    }
                                  }}
                                />
                              </Box>
                              <Box sx={{ pl: 5 }}>
                                <Chip
                                  label={`Track ${train.trackNumber}`}
                                  size="small"
                                  sx={{
                                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                    color: '#548dd4',
                                    fontWeight: 600,
                                    fontSize: '0.75rem',
                                  }}
                                />
                              </Box>
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    </Popover>
                  </>
                )}

                {/* Account Switcher */}
                {memberAccounts && memberAccounts.length > 1 && (
                  <FormControl
                    variant="outlined"
                    size="small"
                    sx={{
                      minWidth: 120,
                      mr: 2,
                      display: { xs: 'none', sm: 'inline-flex' },
                      backgroundColor: "rgba(255, 255, 255, 0.15)",
                      borderRadius: 1,
                      "& .MuiOutlinedInput-root": {
                        color: "white",
                      },
                      "& .MuiSelect-icon": {
                        color: "white",
                      },
                    }}
                  >
                    <Select
                      value={currentAccount}
                      onChange={handleAccountChange}
                      displayEmpty
                      inputProps={{ "aria-label": "Switch account" }}
                      sx={{ color: "white", pl: 1 }}
                    >
                      {memberAccounts.map((account) => (
                        <MenuItem key={account.id} value={account.id}>
                          {account.display_name || `Account ${account.id}`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                <Tooltip title="Open settings">
                  <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                    <Avatar alt={`${firstName} ${lastName}`}>
                      {getInitials(firstName, lastName)}
                    </Avatar>
                  </IconButton>
                </Tooltip>
                <Menu
                  sx={{ mt: "45px" }}
                  id="menu-appbar"
                  anchorEl={anchorElUser}
                  anchorOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  open={Boolean(anchorElUser)}
                  onClose={handleCloseUserMenu}
                >
                  {/* Add account switcher to profile menu on mobile */}
                  {memberAccounts && memberAccounts.length > 1 && (
                    <Box sx={{ display: { xs: 'block', sm: 'none' }, px: 2, py: 1 }}>
                      <FormControl fullWidth size="small">
                        <InputLabel id="account-select-label">Account</InputLabel>
                        <Select
                          labelId="account-select-label"
                          value={currentAccount}
                          label="Account"
                          onChange={handleAccountChange}
                        >
                          {memberAccounts.map((account) => (
                            <MenuItem key={account.id} value={account.id}>
                              {account.display_name || `Account ${account.id}`}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  )}
                  
                  {/* Add admin mode toggle to profile menu on mobile */}
                  {isAdminMode && (
                    <MenuItem 
                      sx={{ display: { xs: 'block', sm: 'none' } }}
                      onClick={handleAdminModeToggle}
                    >
                      <Typography textAlign="center">Exit Admin Mode</Typography>
                    </MenuItem>
                  )}
                  
                  {dynamicSettings.map((setting) => (
                    <MenuItem
                      key={setting}
                      onClick={() => handleSettingClick(setting)}
                    >
                      <Typography sx={{ textAlign: "center" }}>
                        {setting}
                      </Typography>
                    </MenuItem>
                  ))}
                </Menu>
              </>
            ) : (
              <Button
                color="inherit"
                onClick={() => navigate("/login")}
                sx={{ ml: 2 }}
              >
                Login
              </Button>
            )}
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
}

export default NavBar;
